import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

const SHOWCASE = [
  {
    title: 'Shakti Education Trust',
    domain: 'phenomenal-conkies-602768.netlify.app/',
    url: 'https://phenomenal-conkies-602768.netlify.app',
    desc: 'A management system for Trust Peoples to handle students, faculties, storytelling, students fee, and courses on single platform.',
    tags: ['Branding', 'Management', 'UX'],
    accent: '#8FD6A7',
    accentSoft: 'rgba(143,214,167,0.35)',
    accentFaint: 'rgba(143,214,167,0.15)',
    testimonial:
      'The preview helped us see the premium direction immediately, and the final storefront felt like a real brand upgrade.',
    person: 'Praveen Patidar',
    role: 'Head of Shakti Pump',
  },
  {
    title: 'Beta AI Assistant',
    domain: 'exquisite-crumble-db7047.netlify.app/',
    url: 'https://exquisite-crumble-db7047.netlify.app/',
    desc: 'An intelligent, adaptive knowledge assistant that transforms your proprietary data into instant, actionable insights to streamline decision-making and boost team productivity.',
    tags: ['AI Agent', 'RAG', 'Automation'],
    accent: '#E59F54',
    accentSoft: 'rgba(229,159,84,0.35)',
    accentFaint: 'rgba(229,159,84,0.15)',
    testimonial:
      'Empower your team with a conversational AI that instantly navigates complex documentation to deliver precise, real-time answers.',
    person: 'Shivam Sengar',
    role: 'CEO, Beta Startup',
  },
  {
    title: 'Mandi Mangement System',
    domain: 'spectacular-pony-66c32d.netlify.app/',
    url: 'https://spectacular-pony-66c32d.netlify.app/',
    desc: 'Bills Management, Cloud access, SMS/Whatsapp integration sales dashboard with GST reporting, payment tracking, and owner-friendly analytics.',
    tags: ['ERP', 'Analytics', 'Cloud'],
    accent: '#56BAF7',
    accentSoft: 'rgba(86,186,247,0.35)',
    accentFaint: 'rgba(86,186,247,0.15)',
    testimonial:
      'We finally had one place to track bills, merchants, and outstanding payments without bouncing between spreadsheets.',
    person: 'Ramswaroop Patidar',
    role: 'Director, Krishna Traders',
  },
];

export default function Showcase() {
  const ref = useRef(null);
  const browserRef = useRef(null);
  const detailsRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const [showPreviewFallback, setShowPreviewFallback] = useState(false);
  const scrollLockRef = useRef(null);

  useEffect(() => {
    // Fade in animations on mount - no ScrollTrigger needed
    setTimeout(() => {
      gsap.from(ref.current.querySelector('.showcase-head'), {
        opacity: 0,
        y: 28,
        duration: 0.7,
        ease: 'power3.out',
      });

      gsap.from(ref.current.querySelector('.showcase-workbench'), {
        opacity: 0,
        y: 34,
        duration: 0.85,
        ease: 'power3.out',
      });
    }, 100);
  }, []);



  const handleProjectClick = (index) => {
    // Lock scroll position before loading new iframe
    scrollLockRef.current = window.scrollY;
    setActiveIndex(index);
  };

  useEffect(() => {
    if (!browserRef.current || !detailsRef.current) return;

    gsap.fromTo(
      browserRef.current,
      { opacity: 0, y: 18 },
      { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }
    );

    gsap.fromTo(
      detailsRef.current,
      { opacity: 0, filter: 'blur(8px)', y: 14 },
      { opacity: 1, filter: 'blur(0px)', y: 0, duration: 0.55, ease: 'power2.out' }
    );
  }, [activeIndex]);

  useEffect(() => {
    setIsPreviewReady(false);
    setShowPreviewFallback(false);

    // Lock scroll while iframe is loading to prevent iframe's auto-scroll from affecting parent page
    const preventScroll = (e) => {
      if (scrollLockRef.current !== null) {
        window.scrollTo(0, scrollLockRef.current);
      }
    };

    window.addEventListener('scroll', preventScroll, { passive: false });

    const fallbackTimer = window.setTimeout(() => {
      setShowPreviewFallback(true);
    }, 4500);

    return () => {
      window.removeEventListener('scroll', preventScroll);
      window.clearTimeout(fallbackTimer);
      scrollLockRef.current = null;
    };
  }, [activeIndex]);

  const active = SHOWCASE[activeIndex];

  return (
    <section id="showcase" ref={ref} className="section section-alt">
      <div className="container">
        <div className="section-heading showcase-head">
          <p className="eyebrow">Portfolio Preview</p>
          <h2 className="heading">
            A cleaner product preview,
            <span className="gradient-text"> built for trust.</span>
          </h2>
          <p className="showcase-hint">
            Click a project card to view it in the live preview.
          </p>
        </div>

        <div className="showcase-workbench">
          <article className="showcase-browser-card" ref={browserRef}>
            <div className="showcase-browser-topbar">
              <div className="preview-browser-dots">
                <span className="browser-dot" />
                <span className="browser-dot" />
                <span className="browser-dot" />
              </div>
              <div className="showcase-browser-pill">{active.domain}</div>
            </div>

            <div className="showcase-browser-window">
              {!isPreviewReady && !showPreviewFallback && (
                <div className="showcase-preview-loading">Loading preview...</div>
              )}

              <iframe
                key={`showcase-${activeIndex}`}
                title={`${active.title} preview`}
                src={active.url}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                referrerPolicy="no-referrer"
                loading="eager"
                onLoad={() => {
                  setIsPreviewReady(true);
                  setShowPreviewFallback(false);
                  // Unlock scroll after iframe finishes loading
                  scrollLockRef.current = null;
                }}
              />

              {showPreviewFallback && !isPreviewReady && (
                <div className="showcase-preview-fallback">
                  <p>Preview is blocked by this website.</p>
                  <p>Open the full site in a new tab.</p>
                </div>
              )}

              <a
                className="showcase-preview-clickthrough"
                href={active.url}
                target="_blank"
                rel="noreferrer"
                aria-label={`Open ${active.title} in new tab`}
              >
                <span>Open full website</span>
              </a>
            </div>

            <a
              className="preview-inline-link"
              href={active.url}
              target="_blank"
              rel="noreferrer"
            >
              Open demo tab
            </a>
          </article>

          <article className="showcase-details-card" ref={detailsRef}>
            <p className="preview-copy-label">Selected project</p>
            <h3>{active.title}</h3>
            <p className="preview-copy-text">{active.desc}</p>

            <div className="preview-tags">
              {active.tags.map((tag) => (
                <span
                  key={tag}
                  className="tag"
                  style={{
                    '--accent': active.accent,
                    '--accent-soft': active.accentSoft,
                    '--accent-faint': active.accentFaint,
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>

            <blockquote className="preview-quote-card">
              <p>“{active.testimonial}”</p>
              <footer>
                <strong>{active.person}</strong>
                <span>{active.role}</span>
              </footer>
            </blockquote>

            <p className="showcase-project-help">Click a project card to switch the live preview.</p>
            <div className="showcase-project-list">
              {SHOWCASE.map((item, index) => (
                <button
                  key={item.title}
                  type="button"
                  className={`showcase-project-item ${index === activeIndex ? 'is-active' : ''}`}
                  onClick={() => handleProjectClick(index)}
                  aria-pressed={index === activeIndex}
                  title="Click to load this project preview"
                >
                  <span className="showcase-project-title">{item.title}</span>
                  <span className="showcase-project-domain">{item.domain}</span>
                </button>
              ))}
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
